/**
 * EmbeddedListTable — ListTable section inside EntityListShell (multi-section pages).
 * Read-only by default; same chrome as Items list.
 */

import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { ListTable, type ListTableColumnDef, type ListTableSortSpec } from './ListTable';

export interface EmbeddedListTableProps<T> {
  title?: string;
  description?: ReactNode;
  items: T[];
  getId: (item: T) => string;
  columns: ListTableColumnDef<T>[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  sort?: ListTableSortSpec[];
  onSortChange?: (specs: ListTableSortSpec[]) => void;
  onRowClick?: (item: T) => void;
  selectable?: boolean;
  className?: string;
  getRowHeight?: (item: T) => number;
}

export function EmbeddedListTable<T>({
  title,
  description,
  items,
  getId,
  columns,
  loading = false,
  emptyTitle = 'No records',
  emptyDescription,
  sort = [],
  onSortChange = () => {},
  onRowClick = () => {},
  selectable = false,
  className,
  getRowHeight,
}: EmbeddedListTableProps<T>) {
  return (
    <section className={className}>
      {title && <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">{title}</h2>}
      {description && (
        <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-2">{description}</div>
      )}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--text-ter)]" />
        </div>
      ) : items.length === 0 ? (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 py-6 text-center">
          <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">{emptyTitle}</p>
          {emptyDescription && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-1">{emptyDescription}</p>
          )}
        </div>
      ) : (
        <ListTable
          rows={items}
          getId={getId}
          columns={columns}
          density="default"
          selectedIds={new Set()}
          onToggleRow={() => {}}
          onSelectAll={() => {}}
          onClearSelection={() => {}}
          onRowClick={onRowClick}
          sort={sort}
          onSortChange={onSortChange}
          selectable={selectable}
          getRowHeight={getRowHeight}
        />
      )}
    </section>
  );
}
